import type { Item, ResumeItem, WatchStateResource } from "@/types/api";
import { formatEpisodeCode } from "@/lib/mediaFormat";

export interface CardWatchProgress {
  progress: number;
  label?: string;
  title?: string;
  updatedAt?: string | null;
}

export function buildWatchStateResourceMap(states: WatchStateResource[]): Map<string, WatchStateResource> {
  return new Map(states.map((state) => [state.attributes.item_id, state]));
}

export function watchStateProgress(state: WatchStateResource | null | undefined): number | undefined {
  if (!state || state.attributes.watched || state.attributes.position_ms <= 0) {
    return undefined;
  }
  return state.attributes.progress_percent;
}

export function watchStateCardProgress(
  state: WatchStateResource | null | undefined,
  item?: Item | null,
): CardWatchProgress | undefined {
  const progress = watchStateProgress(state);
  if (progress === undefined) {
    return undefined;
  }
  return {
    progress,
    label: item ? progressLabelForItem(item) : undefined,
    title: item ? progressTitleForItem(item) : undefined,
    updatedAt: state?.attributes.updated_at ?? null,
  };
}

export function buildResumeCardProgressMap(resumeItems: ResumeItem[]): Map<string, CardWatchProgress> {
  const byKey = new Map<string, CardWatchProgress>();

  for (const resumeItem of resumeItems) {
    const embedded = embeddedResumeItem(resumeItem);
    if (!embedded) {
      continue;
    }
    const progress = resumeCardProgress(resumeItem, embedded);
    if (!progress) {
      continue;
    }
    for (const key of resumeProgressKeysForItem(embedded)) {
      setNewestCardProgress(byKey, key, progress);
    }
  }

  return byKey;
}

export function resumeCardProgressForItem(
  item: Item,
  resumeProgressByKey: Map<string, CardWatchProgress>,
): CardWatchProgress | undefined {
  for (const key of displayProgressKeysForItem(item)) {
    const progress = resumeProgressByKey.get(key);
    if (progress) {
      return progress;
    }
  }
  return undefined;
}

export function bestWatchStateCardProgressForItems(
  items: Item[],
  watchStateByItem: Map<string, WatchStateResource>,
): CardWatchProgress | undefined {
  let best: CardWatchProgress | undefined;
  for (const item of items) {
    const progress = watchStateCardProgress(watchStateByItem.get(item.id), item);
    if (!progress) {
      continue;
    }
    if (!best || cardProgressIsNewer(progress, best)) {
      best = progress;
    }
  }
  return best;
}

export function cardProgressForItem(
  item: Item,
  watchStateByItem: Map<string, WatchStateResource>,
  resumeProgressByKey: Map<string, CardWatchProgress>,
): CardWatchProgress | undefined {
  const direct = watchStateCardProgress(watchStateByItem.get(item.id), item);
  if (item.attributes.item_type === "show" || item.attributes.item_type === "season") {
    return resumeCardProgressForItem(item, resumeProgressByKey) ?? direct;
  }
  return direct ?? resumeCardProgressForItem(item, resumeProgressByKey);
}

export function formatPlaybackPosition(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function embeddedResumeItem(resumeItem: ResumeItem): Item | null {
  const itemRef = resumeItem.relationships.item.data;
  return "attributes" in itemRef ? itemRef : null;
}

function resumeCardProgress(resumeItem: ResumeItem, item: Item): CardWatchProgress | undefined {
  const progress = clampProgress(resumeItem.attributes.progress_percent);
  if (progress <= 0) {
    return undefined;
  }
  return {
    progress,
    label: progressLabelForItem(item),
    title: progressTitleForItem(item),
    updatedAt: resumeItem.attributes.position_updated_at,
  };
}

function progressLabelForItem(item: Item): string | undefined {
  if (item.attributes.item_type !== "episode") {
    return undefined;
  }
  return formatEpisodeCode(item) ?? undefined;
}

function progressTitleForItem(item: Item): string | undefined {
  if (item.attributes.item_type !== "episode") {
    return undefined;
  }
  const code = formatEpisodeCode(item);
  const title = item.attributes.episode_title || item.attributes.title;
  if (code && title) {
    return `${code} · ${title}`;
  }
  return code ?? title ?? undefined;
}

function displayProgressKeysForItem(item: Item): string[] {
  const keys = new Set<string>();
  addKey(keys, item.id);
  const attrs = item.attributes;
  if (attrs.item_type === "show") {
    addKey(keys, attrs.series_id);
    addSeriesTitleKey(keys, attrs.series_title);
    addSeriesTitleKey(keys, attrs.title);
  }
  if (attrs.item_type === "season") {
    addKey(keys, attrs.season_id);
    addKey(keys, attrs.series_id);
    addSeriesTitleKey(keys, attrs.series_title);
  }
  return [...keys];
}

function resumeProgressKeysForItem(item: Item): string[] {
  const keys = new Set<string>();
  addKey(keys, item.id);
  const attrs = item.attributes;
  if (attrs.item_type === "episode") {
    addKey(keys, attrs.season_id);
    addKey(keys, attrs.series_id);
    addSeriesTitleKey(keys, attrs.series_title);
  } else if (attrs.item_type === "season") {
    addKey(keys, attrs.season_id);
    addKey(keys, attrs.series_id);
    addSeriesTitleKey(keys, attrs.series_title);
  } else if (attrs.item_type === "show") {
    addKey(keys, attrs.series_id);
    addSeriesTitleKey(keys, attrs.series_title);
    addSeriesTitleKey(keys, attrs.title);
  }
  return [...keys];
}

function addKey(keys: Set<string>, value: string | null | undefined) {
  const normalized = value?.trim();
  if (normalized) {
    keys.add(normalized);
  }
}

function addSeriesTitleKey(keys: Set<string>, value: string | null | undefined) {
  const normalized = value?.trim().toLowerCase();
  if (normalized) {
    keys.add(`series:${normalized}`);
  }
}

function setNewestCardProgress(
  map: Map<string, CardWatchProgress>,
  key: string,
  progress: CardWatchProgress,
) {
  const current = map.get(key);
  if (!current || cardProgressIsNewer(progress, current)) {
    map.set(key, progress);
  }
}

function cardProgressIsNewer(left: CardWatchProgress, right: CardWatchProgress): boolean {
  const leftTime = parseProgressTime(left.updatedAt);
  const rightTime = parseProgressTime(right.updatedAt);
  if (leftTime !== rightTime) {
    return leftTime > rightTime;
  }
  return left.progress > right.progress;
}

function parseProgressTime(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function clampProgress(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, value));
}
